import { useCallback } from 'react';

import VideoPrivacyIcon from 'assets/images/video-privacy.png';
import { SourceMediaTypes } from 'features/source-media-tracks/enums';
import { useSourceMediaTracks } from 'features/source-media-tracks/hooks';

// @todo(PQP): With the new implementation of useSourceMediaTracks, this hook is essentially a straight pass-through
// and we should consider removing it and update the components that use it to use useSourceMediaTracks directly.
export const useMediaStream = () => {
  const { getSourceMediaTrack } = useSourceMediaTracks();

  // Choose video settings based on feature flag resolution value
  // Supports: '720p', '1080p', or undefined/other (defaults to legacy)
  const getInterpAudioStream = useCallback(async () => {
    const interpAudioTrack = await getSourceMediaTrack(
      SourceMediaTypes.INTERP_AUDIO
    );
    return new MediaStream([interpAudioTrack]);
  }, [getSourceMediaTrack]);

  const getInterpVideoStream = useCallback(async () => {
    const interpVideoTrack = await getSourceMediaTrack(
      SourceMediaTypes.INTERP_VIDEO
    );
    return new MediaStream([interpVideoTrack]);
  }, [getSourceMediaTrack]);

  // Returns a self-view stream backed by a cloned track. The clone shares the same camera source
  // but has an independent lifecycle — stopping it does not affect the original INTERP_VIDEO track,
  // and stopping the original does not affect the clone. This makes the self-view immune to RTC
  // cleanup, BackgroundSelfView unmount, or any other service that stops the managed track.
  const getSelfViewStream = useCallback(async () => {
    const interpVideoTrack = await getSourceMediaTrack(
      SourceMediaTypes.INTERP_VIDEO
    );
    const clonedTrack = interpVideoTrack.clone();
    // Re-enable the cloned track if the original was disabled — the clone inherits
    // the enabled state, but we don't want to mutate the original managed track.
    if (clonedTrack && !clonedTrack.enabled) {
      console.log(
        'Debug: Re-enabling cloned INTERP_VIDEO track for new stream'
      );
      clonedTrack.enabled = true;
    }
    return new MediaStream([clonedTrack]);
  }, [getSourceMediaTrack]);

  const getPrivacyMediaStreamTrack = useCallback(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const image = new Image();
    image.src = VideoPrivacyIcon;
    image.onload = () => {
      context?.drawImage(image, 0, 0, 300, 150);
    };
    return canvas.captureStream().getVideoTracks()[0];
  }, []);

  return {
    getInterpAudioStream,
    getInterpVideoStream,
    getSelfViewStream,
    getPrivacyMediaStreamTrack,
  };
};
